// v3.15-1130 — Appointment Awareness Banner.
//
// Operational failure root-causer: the previous notification path emailed
// ONLY the client. The rep had no in-app cue if they weren't watching email.
// This banner pulls /api/appointments/operations-summary every 30s and pins
// itself to the top of the dashboard the moment an appointment is:
//   • starting in <=20 minutes  (red — "starting now / soon")
//   • overdue (start passed, no outcome logged)
//
// Design constraints (per spec): non-blocking, dismissable per-alert (NOT
// per-banner) so a rep can ack an item without losing visibility on others.
// The banner re-appears on the next poll if a NEW alert lands.
//
// Also pulses the browser tab title so reps see the alert even when the
// Hub tab is in the background.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, ChevronRight, X } from 'lucide-react';
import api from '../lib/api';

const POLL_INTERVAL_MS = 30_000;  // 30s — tight enough to catch the 15-min email window
const DISMISS_KEY = 'hub_appt_banner_dismissed_v1';

function loadDismissed() {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}
function saveDismissed(set) {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(set))); } catch { /* noop */ }
}

const formatLocalTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return iso; }
};
const minutesUntil = (iso) => {
  if (!iso) return null;
  try { return Math.round((new Date(iso).getTime() - Date.now()) / 60000); }
  catch { return null; }
};

export default function AppointmentAlertBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(loadDismissed());
  const prevTitleRef = useRef(typeof document !== 'undefined' ? document.title : '');

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/appointments/operations-summary');
      setData(res.data || null);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSummary]);

  // Compute visible alerts (not yet dismissed in this session).
  const startingSoon = (data?.starting_soon || []).filter(a => !dismissed.has(a.id));
  const overdue      = (data?.overdue || []).filter(a => !dismissed.has(a.id));
  const totalActive  = startingSoon.length + overdue.length;

  // Pulse the browser tab title when an alert is active.
  useEffect(() => {
    const original = prevTitleRef.current || 'PTC Client Hub';
    if (totalActive === 0) {
      document.title = original;
      return undefined;
    }
    const next = startingSoon[0] || overdue[0];
    if (!next) {
      document.title = original;
      return undefined;
    }
    const label = startingSoon.length > 0
      ? (() => {
          const m = minutesUntil(next.date_time_start);
          if (m == null) return 'Appointment starting soon';
          if (m <= 0)    return 'Appointment starting NOW';
          return `Appointment in ${m} min`;
        })()
      : 'Overdue appointment';
    document.title = `(!) ${label} — ${next.client_name || 'Hub'}`;
    return () => { document.title = original; };
  }, [totalActive, startingSoon, overdue]);

  const dismissOne = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  const openClient = (clientId) => {
    if (clientId) navigate(`/clients/${clientId}`);
  };

  if (totalActive === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="appointment-alert-banner">
      {startingSoon.map((a) => {
        const m = minutesUntil(a.date_time_start);
        const isNow = m != null && m <= 0;
        const label = isNow
          ? 'STARTING NOW'
          : (m === 1 ? 'Starting in 1 minute' : `Starting in ${m ?? '—'} minutes`);
        return (
          <div
            key={a.id}
            data-testid={`appt-alert-soon-${a.id}`}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-red-300 bg-red-50 shadow-sm animate-pulse-slow"
            style={{ animation: isNow ? 'pulse-ring 1.6s ease-in-out infinite' : undefined }}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center">
              <Clock className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider text-red-700">
                {label}
              </div>
              <div className="text-sm font-semibold text-red-900 truncate">
                {a.client_name || 'Unknown client'}
                {a.assigned_to_name ? <span className="text-red-700 font-normal"> — {a.assigned_to_name}</span> : null}
              </div>
              <div className="text-xs text-red-700">
                {formatLocalTime(a.date_time_start)}
                {a.notes ? <span className="text-red-600/80"> · {a.notes}</span> : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => openClient(a.client_id)}
              className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
              data-testid={`appt-alert-open-${a.id}`}
            >
              Open <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => dismissOne(a.id)}
              className="p-1.5 rounded-md text-red-700 hover:bg-red-100"
              aria-label="Dismiss"
              data-testid={`appt-alert-dismiss-${a.id}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      {overdue.map((a) => {
        const m = minutesUntil(a.date_time_start);
        const lateBy = m == null ? null : -m;
        const label = lateBy == null
          ? 'OVERDUE — no outcome logged'
          : `OVERDUE by ${lateBy} min — no outcome logged`;
        return (
          <div
            key={a.id}
            data-testid={`appt-alert-overdue-${a.id}`}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-amber-300 bg-amber-50 shadow-sm"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-700">{label}</div>
              <div className="text-sm font-semibold text-amber-900 truncate">
                {a.client_name || 'Unknown client'}
                {a.assigned_to_name ? <span className="text-amber-700 font-normal"> — {a.assigned_to_name}</span> : null}
              </div>
              <div className="text-xs text-amber-700">
                Scheduled {formatLocalTime(a.date_time_start)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => openClient(a.client_id)}
              className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
              data-testid={`appt-alert-open-${a.id}`}
            >
              Log Outcome <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => dismissOne(a.id)}
              className="p-1.5 rounded-md text-amber-700 hover:bg-amber-100"
              aria-label="Dismiss"
              data-testid={`appt-alert-dismiss-${a.id}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
